// User Management Component
// Phase 10: Admin Dashboard
// © 2025 precatio, llc

import React, { useState, useEffect } from 'react';

const UserManagement = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [showTierModal, setShowTierModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [search, statusFilter, tierFilter, pagination.offset]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset
      });
      
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (tierFilter) params.append('tier', tierFilter);

      const response = await fetch(`/api/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setPagination(prev => ({ ...prev, total: data.pagination.total }));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (userId) => {
    if (!window.confirm('Unlock this user account?')) return;

    try {
      const response = await fetch(`/api/admin/users/${userId}/unlock`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Account unlocked');
        fetchUsers();
      }
    } catch (error) {
      console.error('Error unlocking account:', error);
      alert('Failed to unlock account');
    }
  };

  const handleTierChange = async (userId, tier, status) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/tier`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier, status })
      });

      if (response.ok) {
        alert('Tier updated successfully');
        setShowTierModal(null);
        fetchUsers();
      } else {
        alert('Failed to update tier');
      }
    } catch (error) {
      console.error('Error updating tier:', error);
      alert('Failed to update tier');
    }
  };

  const handleDelete = async (userId) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('User deleted');
        setShowDeleteModal(null);
        fetchUsers();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="user-management">
      <h2>User Management</h2>

      {/* Search and Filters */}
      <div className="user-controls">
        <input
          type="text"
          className="user-search"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="user-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="none">Free</option>
          <option value="trial">Trial</option>
          <option value="active">Active</option>
        </select>
        <select
          className="user-filter"
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
        >
          <option value="">All Tiers</option>
          <option value="pilgrim">Pilgrim</option>
          <option value="disciple">Disciple</option>
          <option value="companion">Companion</option>
          <option value="retreatant">Retreatant</option>
        </select>
      </div>

      {/* User Table */}
      {loading ? (
        <div className="admin-loading">Loading users...</div>
      ) : (
        <div className="user-table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Tier</th>
                <th>Joined</th>
                <th>Admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#a0a0a0' }}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td className="user-email">{user.email}</td>
                    <td>
                      <span className={`user-status ${user.subscription_status}`}>
                        {user.subscription_status || 'none'}
                      </span>
                    </td>
                    <td>
                      <span className={`user-tier ${user.subscription_tier}`}>
                        {user.subscription_tier || 'pilgrim'}
                      </span>
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>{user.is_admin ? '✓' : ''}</td>
                    <td>
                      <div className="user-actions">
                        <button
                          className="action-btn action-btn-tier"
                          onClick={() => setShowTierModal(user)}
                        >
                          Change Tier
                        </button>
                        {user.account_locked && (
                          <button
                            className="action-btn action-btn-unlock"
                            onClick={() => handleUnlock(user.id)}
                          >
                            Unlock
                          </button>
                        )}
                        <button
                          className="action-btn action-btn-delete"
                          onClick={() => setShowDeleteModal(user)}
                          disabled={user.is_admin}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <div className="pagination-info">
              Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={pagination.offset === 0}
              >
                Previous
              </button>
              <button
                className="pagination-btn"
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={pagination.offset + pagination.limit >= pagination.total}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tier Change Modal */}
      {showTierModal && (
        <div className="modal-overlay" onClick={() => setShowTierModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Change User Tier</h3>
            <p style={{ color: '#a0a0a0', marginBottom: '1rem' }}>
              User: <strong style={{ color: '#d4af37' }}>{showTierModal.email}</strong>
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const tier = e.target.tier.value;
                const status = e.target.status.value;
                handleTierChange(showTierModal.id, tier, status);
              }}
            >
              <label className="modal-label">Tier</label>
              <select
                name="tier"
                className="modal-select"
                defaultValue={showTierModal.subscription_tier || 'pilgrim'}
              >
                <option value="pilgrim">Pilgrim (Free)</option>
                <option value="disciple">Disciple ($4.99/mo)</option>
                <option value="companion">Companion ($9.99/mo)</option>
                <option value="retreatant">Retreatant ($14.99/mo)</option>
              </select>

              <label className="modal-label">Status</label>
              <select
                name="status"
                className="modal-select"
                defaultValue={showTierModal.subscription_status || 'none'}
              >
                <option value="none">Free (No subscription)</option>
                <option value="trial">Trial</option>
                <option value="active">Active (Paid)</option>
              </select>

              <div className="modal-actions">
                <button type="submit" className="modal-btn modal-btn-primary">
                  Save Changes
                </button>
                <button
                  type="button"
                  className="modal-btn modal-btn-secondary"
                  onClick={() => setShowTierModal(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Delete User</h3>
            <p style={{ color: '#e0e0e0', marginBottom: '1rem' }}>
              Are you sure you want to delete <strong style={{ color: '#d4af37' }}>{showDeleteModal.email}</strong>?
            </p>
            <p style={{ color: '#f44336', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              This will permanently delete the user and all their conversations. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="modal-btn modal-btn-danger"
                onClick={() => handleDelete(showDeleteModal.id)}
              >
                Delete User
              </button>
              <button
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowDeleteModal(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
